import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NgxChartsModule, LegendPosition, ScaleType } from '@swimlane/ngx-charts';
import { Color } from '@swimlane/ngx-charts';
import { StatisticsService } from '../../../core/services/api-services/statistics.service';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { ClientOnboardingChartComponent } from "../../charts/client-onboarding-chart/client-onboarding-chart.component";
import { WeeklyWageWithdrawalChartComponent } from "../../charts/weekly-wage-withdrawal-chart/weekly-wage-withdrawal-chart.component";
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { BreadcrumbComponent, BreadcrumbItem } from "../../../shared/components/breadcrumb/breadcrumb.component";
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subscription } from 'rxjs';
@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, NgxChartsModule, ClientOnboardingChartComponent, WeeklyWageWithdrawalChartComponent, BreadcrumbComponent],
  templateUrl: './admin-dashboard.component.html',
  styleUrls: ['./admin-dashboard.component.css'],
})
export class AdminDashboardComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  dataSubscriber$!: Subscription;

  // ── Existing demo data (kept; safe to ignore if unused) ───────────────────────
  summaryCards = [
    { count: 12, label: 'Client requests', route: '/client-requests' },
    { count: 2, label: 'User Approvals', route: '/user-approval' },
    { count: 20, label: 'Credit Approvals', route: '/credit-approvals' },
    { count: 2, label: 'Investment Approvals', route: '/investment-approval' },
    { count: 5, label: 'Risk flags', route: '/risk-flags' },
    { count: 4, label: 'Risk & Compliance flags', route: '/compliance-flags' },
  ];

  customerTrend = {
    columns: ['01/01/2025', '01/02/2025', '01/03/2025', 'PCP 1Yr', 'PCP 6Mnth'],
    rows: [
      {
        section: 'Registered',
        data: [
          { label: 'Employers', values: [30, 35, 40, 10, 15] },
          { label: 'Employees', values: [500, 600, 700, 200, 350] },
        ],
      },
      {
        section: 'Active',
        data: [
          { label: 'Employers', values: [20, 22, 25, 5, 7] },
          { label: 'Employees', values: [300, 400, 450, 150, 200] },
        ],
      },
    ],
  };

  liquidityPosition = [
    { label: 'Total Employee Redemptions', amount: 'LKR 78,000,000.00', value: 78, color: '#FAD7C6' },
    { label: 'Total Employer Rollovers', amount: 'LKR 52,000,000.00', value: 52, color: '#42D0C3' },
    { label: 'Total outstanding advances', amount: 'LKR 130,000,000.00', value: 130, color: '#FFBD59' },
    { label: 'Active investments', amount: 'LKR 145,000,000.00', value: 145, color: '#60C660' },
    { label: 'Net liquidity position', amount: 'LKR 15,000,000.00', value: 15, color: '#F14C4C' },
    { label: 'Cash at bank', amount: 'LKR 9,000,000.00', value: 9, color: '#D3E3F7' },
  ];

  donutChartData: any[] = [];
  colorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#FAD7C6', '#42D0C3', '#FFBD59', '#60C660', '#F14C4C', '#D3E3F7']
  };
  legendPosition: LegendPosition = LegendPosition.Right;

  // ── Dashboard API state ───────────────────────────────────────────────────────
  isDashboardLoading = false;
  dashboardError = '';
  dashboard: any = null;

  // Cards that the HTML binds to (built from `dashboard`)
  dashboardCards: Array<{ label: string; value: number; route?: string; isMoney?: boolean; queryParams?: Record<string, string> }> = [];

  constructor(private router: Router, private statisticsService: StatisticsService, private authService: AuthService, private breadcrumbService: BreadcrumbService) {
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {
    // keep existing chart demo mapping if used somewhere else
    this.donutChartData = this.liquidityPosition.map(item => ({
      name: item.label,
      value: item.value,
    }));

    // Load real dashboard payload
    this.fetchDashboardStats();

    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' }
    ]);
  }

  navigateTo(route: string | undefined, count: number, queryParams?: Record<string, string>): void {
    if (!route) return;
    this.router.navigate([route], { state: { count }, queryParams });
  }

  // ── API: GET /statistics/dashboard ────────────────────────────────────────────
  fetchDashboardStats(): void {
    this.isDashboardLoading = true;
    this.dashboardError = '';

    this.dataSubscriber$.add(
      this.statisticsService.getDashboardStats().subscribe({
        next: (response: any) => {
          if (response?.success || this.isPlainPayload(response)) {
            // Accept either { success, data } or the plain object you showed.
            const data = response?.data ?? response ?? null;
            this.dashboard = data;

            // Build cards for the template
            this.dashboardCards = this.buildDashboardCards(data);
          } else {
            this.dashboardError = response?.message || 'Failed to load dashboard stats.';
            this.dashboardCards = [];
          }
          this.isDashboardLoading = false;
        },
        error: (err: any) => {
          this.dashboardError = 'API error (dashboard): ' + (err?.message ?? err);
          this.isDashboardLoading = false;
          this.dashboardCards = [];
        }
      })
    );
  }

  // Accepts the raw object shape you provided:
  // {
  //   clientOnboardingRequests: 0, rolloverRequests: 0,
  //   investmentsRequests: 0, riskFlags: 0, riskAndComplianceFlags: 0,
  //   cumulativeIncome: 1818.70
  // }
  private buildDashboardCards(d: any) {
    if (!d) return [];

    const routeIf = (moduleKey: string, route: string) =>
      this.authService.canView(moduleKey) ? route : '';

    return [
      // Clients → Employer list
      {
        label: 'Client Onboarding Requests',
        value: +d.clientOnboardingRequests || 0,
        route: routeIf('clients.employer', '/client/employer'),
      },

      // Credit & Risk → Manage rollovers
      {
        label: 'Rollover Requests',
        value: +d.rolloverRequests || 0,
        route: routeIf('creditRisk.manageRollovers', '/credit-and-risk/rollover-management'),
      },
      // Credit & Risk → Manage rollovers
      {
        label: 'Credit Management',
        value: +d.manageCredit || 0,
        route: routeIf('creditRisk.manageCredit', '/credit-and-risk/credit-management'),
      },

      // Not wired yet (keep empty route)
      {
        label: 'Investment Requests',
        value: +d.investmentsRequests || 0,
        route: '',
      },

      // Risk flags (point to Ops Risk page if they can view it)
      {
        label: 'Risk Flags',
        value: +d.riskFlags || 0,
        route: routeIf('opsRisk', '/ops-risk-and-compliance'),
      },

      // // Ops Risk & Compliance flags
      // {
      //   label: 'Risk & Compliance Flags',                          // Commented due to currently no such field or data - 2026-02-02
      //   value: +d.riskAndComplianceFlags || 0,
      //   route: routeIf('opsRisk', '/ops-risk-and-compliance'),
      // },

      // Treasury → Cumulative Income
      {
        label: 'Cumulative Income',
        value: +d.cumulativeIncome || 0,
        route: routeIf('treasury.cumulativeIncome', '/treasury/cumulative-income'),
        isMoney: true,
      },

      // Treasury → Wage Withdrawals
      {
        label: 'Wage Withdrawals',
        value: +d.wageWithdrawals || 0,
        route: routeIf('treasury.wageWithdrawals', '/treasury/wage-Withdrawals'),
        isMoney: true,
      },

      // Not wired yet (keep empty route)
      {
        label: 'Total Wallet Balance',
        value: +d.totalWalletAmount || 0,
        route: '',
        isMoney: true,
      },

      // Not wired yet (keep empty route)
      {
        label: 'Total Wallet Withdrawal',
        value: +d.activeTotalWalletWithdrawal || 0,
        route: routeIf('treasury.walletWithdrawals', '/treasury/wallet-withdrawals'),
        isMoney: true,
      },

      // Not wired yet (keep empty route)
      {
        label: 'Cumulative Cost of Funds',
        value: 0,
        route: '',
        isMoney: true,
      },

      // Manage Payments → Employer Outstanding Payments
      {
        label: 'Employer Outstanding Payments',
        value: +d.employerOutstandingPayments || 0,
        route: routeIf('manageCixorPayments.paymentHistory', '/manageCixorPayments/payment-history'),
        isMoney: true,
      },

      // Manage Payments → PayDay Payment Approvals
      {
        label: 'PayDay Payment Approvals',
        value: +d.payDayPaymentApprovalCount || 0,
        route: routeIf('manageCixorPayments.cixorPayments', '/manageCixorPayments/cixor-payments'),
        queryParams: { tab: 'pending' },
      },

      // Manage Payments → Payroll Payment Approvals
      {
        label: 'Payroll Payment Approvals',
        value: +d.payrollPaymentApprovalCount || 0,
        route: routeIf('manageCixorPayments.payrollPayments', '/manageCixorPayments/payroll-payments'),
        queryParams: { tab: 'pending' },
      },
    ];
  }


  // Helper: detect if the response is the plain object (no {success,data})
  private isPlainPayload(obj: any): boolean {
    return obj
      && typeof obj === 'object'
      && 'clientOnboardingRequests' in obj
      && 'cumulativeIncome' in obj;
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }
}
